import React from 'react';
import {
  Box, Typography, Card, Button, Divider, CircularProgress,
} from '@mui/material';
import { ImageOutlined, CameraAltOutlined } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

export const PRIMARY = '#1075BA';

export const SWITCH_SX = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: '#00a882' },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { background: '#00a882' },
};

export const INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2, background: '#f8f9fb',
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(PRIMARY, 0.4) },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e4e8ef' },
};

// ── TabPanel ─────────────────────────────────────────────────────────────────
export function TabPanel({ value, index, children }) {
  return value === index ? <Box>{children}</Box> : null;
}

// ── FormField ─────────────────────────────────────────────────────────────────
export function FormField({ label, hint, children, required }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a2e', mb: 0.75, fontSize: '0.875rem' }}>
        {label}{required && <Box component="span" sx={{ color: '#d32f2f', ml: 0.5 }}>*</Box>}
      </Typography>
      {children}
      {hint && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{hint}</Typography>}
    </Box>
  );
}

// ── SectionCard ───────────────────────────────────────────────────────────────
export function SectionCard({ title, subtitle, icon, children }) {
  return (
    <Card sx={{ borderRadius: 3, border: '1px solid #e8edf2', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', mb: 2.5 }}>
      {(title || subtitle) && (
        <Box sx={{ px: 3, pt: 2.5, pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.5 }}>
            {icon && <Box sx={{ color: PRIMARY, display: 'flex' }}>{icon}</Box>}
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a1a2e' }}>{title}</Typography>
          </Box>
          {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
          <Divider sx={{ mt: 2, borderColor: '#f0f2f5' }} />
        </Box>
      )}
      <Box sx={{ p: 3 }}>{children}</Box>
    </Card>
  );
}

// ── ImageUploader ─────────────────────────────────────────────────────────────
export function ImageUploader({ value, onChange, aspectRatio = '16/5', label = 'Upload Image', hint = 'PNG, JPG or WebP. Max 5 MB.' }) {
  const inputRef = React.useRef();
  const [uploading, setUploading] = React.useState(false);

  const handleFile = async (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setUploading(true);
    const localUrl = URL.createObjectURL(file);
    onChange(localUrl);
    setUploading(false);
  };

  return (
    <Box>
      <Box
        onClick={() => !uploading && inputRef.current?.click()}
        onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
        onDragOver={e => e.preventDefault()}
        sx={{
          width: '100%', aspectRatio,
          border: `2px dashed ${value ? 'transparent' : alpha(PRIMARY, 0.25)}`,
          borderRadius: 2.5,
          background: value ? 'transparent' : alpha(PRIMARY, 0.03),
          cursor: uploading ? 'default' : 'pointer',
          position: 'relative', overflow: 'hidden',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.15s',
          '&:hover': uploading ? {} : { border: `2px dashed ${alpha(PRIMARY, 0.55)}`, background: alpha(PRIMARY, 0.06) },
        }}
      >
        {uploading ? (
          <CircularProgress size={28} sx={{ color: PRIMARY }} />
        ) : value ? (
          <>
            <Box component="img" src={value} alt="preview"
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            <Box sx={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0)', transition: 'background 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { background: 'rgba(0,0,0,0.38)' } }}>
              <CameraAltOutlined sx={{ color: '#fff', fontSize: 28, opacity: 0 }} />
            </Box>
          </>
        ) : (
          <Box sx={{ textAlign: 'center', px: 2 }}>
            <ImageOutlined sx={{ fontSize: 32, color: alpha(PRIMARY, 0.3), mb: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: alpha(PRIMARY, 0.55) }}>{label}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Drag & drop or click to browse</Typography>
          </Box>
        )}
      </Box>
      {value && (
        <Button size="small" onClick={() => onChange('')}
          sx={{ mt: 0.75, fontSize: '0.75rem', color: '#d32f2f', '&:hover': { background: alpha('#d32f2f', 0.06) } }}>
          Remove
        </Button>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{hint}</Typography>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={e => handleFile(e.target.files?.[0])} />
    </Box>
  );
}
